/**
 * WasmOS Bootloader — platform-agnostic orchestrator.
 *
 * Checks prerequisites, allocates the SharedArrayBuffer ("physical RAM"),
 * spawns the kernel Worker, wires up I/O, and auto-launches the shell.
 *
 * All browser/DOM-specific code lives in platform-browser.ts.
 * Package loading (fetching .wpkg files, seeding VFS) is handled by
 * kernel-worker.ts via pkg-loader.ts.
 */

import { BrowserPlatform } from "./platform-browser.js";
import type { Platform } from "./platform.js";
import { WAKE_SLOT } from "../../shared/mailbox.mjs";
import {
  parseWasmosAgentSearchParam,
  DEFAULT_AGENT_LOG_CONFIG,
  AGENT_HOOKS_PROTOCOL,
  AGENT_SESSION_STORAGE_KEY,
  agentEntryMeetsMinSeverity,
} from "../../shared/agent-log.mjs";

const SAB_SIZE = 256 * 1024 * 1024;
const KERNEL_PATH = "kernel.wasm";

// ── Shell map ────────────────────────────────────────────────────────────────

const SHELL_MAP: Record<string, { path: string; name: string }> = {
  sh: { path: "/usr/bin/sh", name: "sh" },
  bash: { path: "/usr/bin/bash", name: "bash" },
};

type WasmOSDebug = {
  outputLog: string;
  seedFile: (path: string, data: Uint8Array | ArrayBuffer | string) => Promise<void>;
  spawnWasm: (name: string, wasm: ArrayBuffer, argv?: string[], installPath?: string) => Promise<number>;
  tracePid: (kernelPid: number, limit?: number) => Promise<void>;
};

type AgentLogLevel = "error" | "warn" | "info" | "debug";
type AgentLogEntry = {
  seq: number;
  t: number;
  level: AgentLogLevel;
  ch: string;
  msg: string;
  kpid?: number;
  cpu?: number;
  hal?: number;
  data?: Record<string, unknown>;
};
type AgentLogConfigSnapshot = {
  channels: { lifecycle: string; worker: string; syscall: string };
  mirrorSyscallToConsole: boolean;
  maxEntries: number;
  maxMsgLen: number;
  maxDebugPerSecond: number;
};
type WorkerSnapshot = {
  workers: Array<{ cpu: number; kernelPid: number; halId: number; mode: string }>;
  tracedSyscalls: Array<{ kernelPid: number; limit: number; baselineDispatch: number }>;
  syscallDispatchTotals: Array<{ kernelPid: number; dispatches: number }>;
};

/** Filter for `getAgentLog(query)`. Results are capped at `limit` (default 2000, max 5000). */
type AgentLogQuery = {
  ch?: string | string[];
  minSeverity?: AgentLogLevel;
  sinceSeq?: number;
  untilSeq?: number;
  kpid?: number;
  cpu?: number;
  hal?: number;
  msgContains?: string;
  limit?: number;
};

type SetAgentLogConfigOpts = Partial<AgentLogConfigSnapshot> & {
  /** Persist current agent-log settings to `sessionStorage` for the tab (merged on next load after URL prefs). */
  persistToSessionStorage?: boolean;
  /** Remove persisted agent-log settings for this tab. */
  clearSessionAgentLogConfig?: boolean;
};

type WasmosEnvironmentCapabilities = {
  agentHooksProtocol: number;
  testHooksEnabled: boolean;
  crossOriginIsolated: boolean;
  sharedArrayBuffer: boolean;
  opfsGetDirectory: boolean;
  kernelWasmPath: string;
  storageEstimateBytes: number | null;
  storagePersisted: boolean | null;
  artifactManifestPresent: boolean;
  imageManifestPresent: boolean;
  wasmFunctionConstructor: boolean;
};

type AgentRuntimeInspection = {
  protocol: number;
  booted: boolean;
  capabilities: WasmosEnvironmentCapabilities;
  agentLog: {
    entryCount: number;
    latestSeq: number;
    config: AgentLogConfigSnapshot;
  };
  terminal: { cols: number; rows: number };
  fontSizePx: number;
  outputLogCharacters: number;
  snapshot: WorkerSnapshot;
};

type WasmOSShellApi = {
  isBooted: () => boolean;
  waitBooted: (timeoutMs?: number) => Promise<void>;
  restartShell: () => void;
};

type WasmOSTerminalApi = {
  injectStdin: (text: string) => void;
  injectStdinUntilOutputContains: (inputLine: string, needle: string, timeoutMs?: number) => Promise<boolean>;
  focusTerminal: () => void;
  getTerminalSize: () => { cols: number; rows: number };
  setFontSize: (px: number) => void;
  getFontSize: () => number;
  getOutputLog: () => string;
  clearOutputLog: () => void;
  waitForOutputContains: (
    needle: string,
    options?: { timeoutMs?: number; intervalMs?: number },
  ) => Promise<void>;
  waitForBootLine: (needle?: string, timeoutMs?: number) => Promise<void>;
  dispatchMouseEventForTest?: (
    col: number,
    row: number,
    opts?: { button?: number; released?: boolean; shiftKey?: boolean; altKey?: boolean; ctrlKey?: boolean; force?: boolean },
  ) => void;
  encodeMouseEventForTest?: (
    col: number,
    row: number,
    opts?: { button?: number; released?: boolean; shiftKey?: boolean; altKey?: boolean; ctrlKey?: boolean },
  ) => string;
};

type WasmOSAgentLogApi = {
  getAgentLog: (query?: AgentLogQuery) => AgentLogEntry[];
  getLatestAgentLogSeq: () => number;
  clearAgentLog: () => void;
  setAgentLogConfig: (opts: SetAgentLogConfigOpts) => void;
  getAgentLogConfig: () => AgentLogConfigSnapshot;
  onAgentLog: (handler: (entry: AgentLogEntry) => void) => () => void;
  waitForAgentLog: (
    predicate: (entry: AgentLogEntry) => boolean,
    options?: { timeoutMs?: number; intervalMs?: number },
  ) => Promise<AgentLogEntry>;
};

type WasmOSDebugApi = {
  getOutputLog: () => string;
  appendOutputLogForTest?: (text: string) => void;
  seedFile: (path: string, data: Uint8Array | ArrayBuffer | string) => Promise<void>;
  spawnWasm: (
    name: string,
    wasm: ArrayBuffer,
    argv?: string[],
    installPath?: string,
  ) => Promise<number>;
  tracePid: (kernelPid: number, limit?: number) => Promise<void>;
  getWorkerSnapshot: () => Promise<WorkerSnapshot>;
  inspectAgentRuntime: () => Promise<AgentRuntimeInspection>;
  quickTraceShell: (opts?: {
    kernelPid?: number;
    traceLimit?: number;
    mirrorToConsole?: boolean;
  }) => Promise<{ kernelPid: number; traceLimit: number }>;
  exportDebugBundle: (opts?: {
    agentLogLimit?: number;
    outputLogMaxChars?: number;
  }) => Promise<Record<string, unknown>>;
  downloadDebugBundle: (opts?: {
    agentLogLimit?: number;
    outputLogMaxChars?: number;
  }) => Promise<void>;
  openPrototypePanel: () => void;
  closePrototypePanel: () => void;
};

type WasmOSStorageApi = {
  flushOpfs: () => Promise<void>;
  wipeOpfs: () => Promise<void>;
};

type WasmOSApi = {
  readonly AGENT_HOOKS_PROTOCOL: number;
  shell: WasmOSShellApi;
  terminal: WasmOSTerminalApi;
  agentLog: WasmOSAgentLogApi;
  debug: WasmOSDebugApi;
  storage: WasmOSStorageApi;
};

function freezeApi<T extends object>(api: T): Readonly<T> {
  return Object.freeze(api);
}

function installReadOnlyGlobal(name: string, value: unknown): void {
  Object.defineProperty(globalThis, name, {
    value,
    enumerable: true,
    configurable: false,
    writable: false,
  });
}

// ── Main boot orchestrator ───────────────────────────────────────────────────

async function main(): Promise<void> {
  const platform: Platform = new BrowserPlatform();

  const results = platform.checkPrerequisites();
  const allOk = platform.renderPrerequisites(results);
  if (!allOk) {
    platform.setStatus("Boot failed: missing prerequisites.", "fail");
    return;
  }
  platform.setStatus("Fetching kernel...");
  let kernelWasm: ArrayBuffer;
  try {
    kernelWasm = await platform.fetchBinary(KERNEL_PATH);
  } catch (err) {
    platform.setStatus(`Boot failed: ${err}`, "fail");
    return;
  }
  platform.setStatus("Allocating physical memory...");
  let sab: SharedArrayBuffer;
  try {
    sab = new SharedArrayBuffer(SAB_SIZE);
  } catch (err) {
    platform.setStatus(`Failed to allocate SharedArrayBuffer: ${err}`, "fail");
    return;
  }
  platform.setStatus(`${SAB_SIZE / (1024 * 1024)} MiB RAM. Spawning kernel...`);
  const sabI32 = new Int32Array(sab);
  const wakeKernelWorker = () => {
    Atomics.store(sabI32, WAKE_SLOT, 1);
    Atomics.notify(sabI32, WAKE_SLOT, 1);
  };

  const agentLogConfig: AgentLogConfigSnapshot = {
    channels: {
      lifecycle: DEFAULT_AGENT_LOG_CONFIG.channels.lifecycle,
      worker: DEFAULT_AGENT_LOG_CONFIG.channels.worker,
      syscall: DEFAULT_AGENT_LOG_CONFIG.channels.syscall,
    },
    mirrorSyscallToConsole: DEFAULT_AGENT_LOG_CONFIG.mirrorSyscallToConsole,
    maxEntries: DEFAULT_AGENT_LOG_CONFIG.maxEntries,
    maxMsgLen: DEFAULT_AGENT_LOG_CONFIG.maxMsgLen,
    maxDebugPerSecond: DEFAULT_AGENT_LOG_CONFIG.maxDebugPerSecond,
  };
  const testHooksEnabled =
    typeof location !== "undefined" &&
    new URLSearchParams(location.search).get("wasmos_test_hooks") === "1";

  function tryMergeSessionAgentHooks(): void {
    try {
      if (typeof sessionStorage === "undefined") return;
      const raw = sessionStorage.getItem(AGENT_SESSION_STORAGE_KEY);
      if (!raw) return;
      const o = JSON.parse(raw) as Record<string, unknown>;
      if (o.channels && typeof o.channels === "object" && o.channels !== null) {
        const ch = o.channels as Record<string, unknown>;
        for (const k of ["lifecycle", "worker", "syscall"] as const) {
          const v = ch[k];
          if (v === "off" || v === "error" || v === "warn" || v === "info" || v === "debug") {
            agentLogConfig.channels[k] = v;
          }
        }
      }
      if (typeof o.mirrorSyscallToConsole === "boolean") {
        agentLogConfig.mirrorSyscallToConsole = o.mirrorSyscallToConsole;
      }
      if (typeof o.maxEntries === "number" && o.maxEntries >= 10 && o.maxEntries <= 20_000) {
        agentLogConfig.maxEntries = o.maxEntries;
      }
      if (typeof o.maxMsgLen === "number" && o.maxMsgLen >= 32 && o.maxMsgLen <= 65536) {
        agentLogConfig.maxMsgLen = o.maxMsgLen;
      }
      if (
        typeof o.maxDebugPerSecond === "number" &&
        o.maxDebugPerSecond >= 1 &&
        o.maxDebugPerSecond <= 10_000
      ) {
        agentLogConfig.maxDebugPerSecond = o.maxDebugPerSecond;
      }
    } catch {
      /* ignore corrupt session blob */
    }
  }

  tryMergeSessionAgentHooks();

  const urlAgentPartial =
    typeof location !== "undefined" ? parseWasmosAgentSearchParam(location.search) : {};
  if (urlAgentPartial.channels) {
    const ch = urlAgentPartial.channels;
    for (const k of ["lifecycle", "worker", "syscall"] as const) {
      const v = ch[k];
      if (v === "off" || v === "error" || v === "warn" || v === "info" || v === "debug") {
        agentLogConfig.channels[k] = v;
      }
    }
  }
  if (typeof urlAgentPartial.mirrorSyscallToConsole === "boolean") {
    agentLogConfig.mirrorSyscallToConsole = urlAgentPartial.mirrorSyscallToConsole;
  }

  let agentLogSeqMain = 0;
  const agentLogRing: AgentLogEntry[] = [];
  const agentLogSubscribers = new Set<(entry: AgentLogEntry) => void>();
  let mainDebugWindowStart = 0;
  let mainDebugCountInWindow = 0;
  let mainLastDebugRateWarnAt = 0;

  function cloneAgentLogEntry(e: AgentLogEntry): AgentLogEntry {
    return { ...e, data: e.data ? { ...e.data } : undefined };
  }

  function appendAgentLogLine(entry: AgentLogEntry): void {
    agentLogRing.push(entry);
    while (agentLogRing.length > agentLogConfig.maxEntries) {
      agentLogRing.shift();
    }
    const copy = cloneAgentLogEntry(entry);
    for (const fn of agentLogSubscribers) {
      try {
        fn(copy);
      } catch {
        /* subscriber must not break logging */
      }
    }
    syncAgentLogTextarea();
  }

  function filterAgentLogRing(query?: AgentLogQuery): AgentLogEntry[] {
    if (!query) {
      return agentLogRing.map(cloneAgentLogEntry);
    }
    const maxOut = Math.min(5000, Math.max(1, query.limit ?? 2000));
    let rows = agentLogRing.map(cloneAgentLogEntry);
    if (query.ch !== undefined) {
      const chs = Array.isArray(query.ch) ? query.ch : [query.ch];
      rows = rows.filter((e) => chs.includes(e.ch));
    }
    if (query.minSeverity !== undefined) {
      rows = rows.filter((e) => agentEntryMeetsMinSeverity(query.minSeverity!, e.level));
    }
    if (query.sinceSeq !== undefined) {
      rows = rows.filter((e) => e.seq > query.sinceSeq!);
    }
    if (query.untilSeq !== undefined) {
      rows = rows.filter((e) => e.seq <= query.untilSeq!);
    }
    if (query.kpid !== undefined) {
      rows = rows.filter((e) => e.kpid === query.kpid);
    }
    if (query.cpu !== undefined) {
      rows = rows.filter((e) => e.cpu === query.cpu);
    }
    if (query.hal !== undefined) {
      rows = rows.filter((e) => e.hal === query.hal);
    }
    if (query.msgContains !== undefined) {
      const needle = query.msgContains;
      rows = rows.filter((e) => e.msg.includes(needle));
    }
    return rows.slice(0, maxOut);
  }

  function syncAgentLogTextarea(): void {
    const ta = document.getElementById("agent-log-text") as HTMLTextAreaElement | null;
    if (!ta) return;
    ta.value = agentLogRing.map((e) => JSON.stringify(e)).join("\n");
    ta.scrollTop = ta.scrollHeight;
  }

  function pushAgentLogEntry(entry: AgentLogEntry): void {
    const now = performance.now();
    if (entry.level === "debug") {
      if (now - mainDebugWindowStart >= 1000) {
        mainDebugWindowStart = now;
        mainDebugCountInWindow = 0;
      }
      if (mainDebugCountInWindow >= agentLogConfig.maxDebugPerSecond) {
        if (now - mainLastDebugRateWarnAt >= 1000) {
          mainLastDebugRateWarnAt = now;
          appendAgentLogLine({
            seq: ++agentLogSeqMain,
            t: now,
            level: "warn",
            ch: "worker",
            msg: "agent log: debug rate limit exceeded on main thread; dropping debug entries",
          });
        }
        return;
      }
      mainDebugCountInWindow += 1;
    }

    appendAgentLogLine(entry);
  }

  const kernelWorker = new Worker("kernel-worker.js", { type: "module" });
  let resolveWhenBooted: (() => void) | null = null;
  const whenBooted = new Promise<void>((resolve) => {
    resolveWhenBooted = resolve;
  });
  let booted = false;
  let shellPid = -1;
  let shellSpawnToken = 0;
  let nextRequestId = 1;
  const pendingSpawn = new Map<number, (pid: number) => void>();
  const pendingDebugSeed = new Map<number, (result: { path: string; error?: string }) => void>();
  const pendingDebugTrace = new Map<number, (result: { kernelPid: number; limit: number }) => void>();
  const pendingOpfsFlush = new Map<number, () => void>();
  const pendingOpfsWipe = new Map<number, (result: { error?: string }) => void>();
  const pendingWorkerSnapshot = new Map<number, (snapshot: WorkerSnapshot) => void>();
  const debugState: WasmOSDebug = {
    outputLog: "",
    seedFile(path, data) {
      const payload = typeof data === "string" ? new TextEncoder().encode(data) : new Uint8Array(data);
      return new Promise<void>((resolve, reject) => {
        const requestId = nextRequestId++;
        pendingDebugSeed.set(requestId, ({ error }) => {
          if (error) {
            reject(new Error(error));
            return;
          }
          resolve();
        });
        const copy = new Uint8Array(payload).buffer;
        kernelWorker.postMessage({ type: "debug-seed-file", requestId, path, data: copy }, [copy]);
      });
    },
    spawnWasm(name, wasm, argv, installPath) {
      return (async () => {
        const targetPath = installPath ?? `/usr/bin/${name}`;
        await debugState.seedFile(targetPath, wasm);
        return requestSpawnPath(targetPath, argv);
      })();
    },
    tracePid(kernelPid, limit = 400) {
      return new Promise<void>((resolve, reject) => {
        const requestId = nextRequestId++;
        pendingDebugTrace.set(requestId, ({ kernelPid: tracedPid, limit: tracedLimit }) => {
          if (tracedPid !== kernelPid || tracedLimit !== limit) {
            reject(new Error(`tracePid(${kernelPid}, ${limit}) failed`));
            return;
          }
          resolve();
        });
        kernelWorker.postMessage({ type: "debug-syscall-trace", requestId, kernelPid, limit });
      });
    },
  };

  const audio = platform.audio;
  let audioReady = false;
  const notifyAudioReady = () => {
    kernelWorker.postMessage({ type: "audio-ready", ready: audioReady });
  };
  if (audio) {
    audio.notifyReady((ready) => {
      audioReady = ready;
      notifyAudioReady();
    });
  }
  notifyAudioReady();
  platform.onUserInteraction(() => {
    void audio?.activate();
  });

  function requestSpawnPath(path: string, argv?: string[]): Promise<number> {
    return new Promise<number>((resolve) => {
      const requestId = nextRequestId++;
      const timeout = setTimeout(() => {
        const pending = pendingSpawn.get(requestId);
        if (!pending) return;
        pendingSpawn.delete(requestId);
        pending(-1);
      }, 5000);
      pendingSpawn.set(requestId, (pid: number) => {
        clearTimeout(timeout);
        pendingSpawn.delete(requestId);
        resolve(pid);
      });
      kernelWorker.postMessage({ type: "spawn-path", requestId, path, argv });
    });
  }

  function cancelPendingSpawn(reasonPid: number): void {
    for (const [requestId, resolve] of pendingSpawn) {
      pendingSpawn.delete(requestId);
      resolve(reasonPid);
    }
  }

  function requestOpfsFlush(): Promise<void> {
    return new Promise<void>((resolve) => {
      const requestId = nextRequestId++;
      pendingOpfsFlush.set(requestId, resolve);
      kernelWorker.postMessage({ type: "flush-opfs", requestId });
    });
  }

  function requestOpfsWipe(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const requestId = nextRequestId++;
      pendingOpfsWipe.set(requestId, ({ error }) => {
        if (error) {
          reject(new Error(error));
          return;
        }
        resolve();
      });
      kernelWorker.postMessage({ type: "wipe-opfs", requestId });
    });
  }

  function injectStdin(text: string): void {
    if (!booted || text.length === 0) return;
    wakeKernelWorker();
    kernelWorker.postMessage({ type: "input", data: text });
  }

  const spawnShell = async () => {
    shellSpawnToken += 1;
    const token = shellSpawnToken;
    cancelPendingSpawn(-1);
    const preferred = platform.getShellChoice();
    const ordered = [preferred, "bash", "sh"];
    const seen = new Set<string>();
    const order: string[] = [];
    for (const item of ordered) {
      if (!SHELL_MAP[item] || seen.has(item)) continue;
      seen.add(item);
      order.push(item);
    }
    const candidates = order;
    const tried: string[] = [];
    for (const choice of candidates) {
      if (token !== shellSpawnToken) return;
      const selected = SHELL_MAP[choice];
      tried.push(selected.name);
      if (token !== shellSpawnToken) return;
      const pid = await requestSpawnPath(selected.path);
      if (token !== shellSpawnToken) return;
      if (pid > 0) {
        shellPid = pid;
        if (choice !== preferred) {
          platform.terminal.write(
            `[boot] shell "${preferred}" unavailable, started "${selected.name}" instead\n`,
          );
        }
        platform.terminal.focus();
        return;
      }
    }
    platform.terminal.write(
      `\n[error] failed to start shell (${preferred}); tried: ${tried.join(", ")}\n` +
      "[hint] Verify wasm assets are available, then reload.\n",
    );
    platform.terminal.focus();
  };

  function restartShellNow(): void {
    if (!booted) return;
    if (shellPid > 0) {
      kernelWorker.postMessage({ type: "kill", pid: shellPid });
      shellPid = -1;
    }
    void spawnShell();
  }

  function postWorkerAgentLogConfig(): void {
    kernelWorker.postMessage({
      type: "set-agent-log-config",
      channels: { ...agentLogConfig.channels },
      mirrorSyscallToConsole: agentLogConfig.mirrorSyscallToConsole,
      maxMsgLen: agentLogConfig.maxMsgLen,
      maxDebugPerSecond: agentLogConfig.maxDebugPerSecond,
    });
  }

  function applyAgentLogConfigPatch(opts: SetAgentLogConfigOpts): void {
    if (opts.channels) {
      for (const k of ["lifecycle", "worker", "syscall"] as const) {
        const v = opts.channels[k];
        if (v === "off" || v === "error" || v === "warn" || v === "info" || v === "debug") {
          agentLogConfig.channels[k] = v;
        }
      }
    }
    if (typeof opts.mirrorSyscallToConsole === "boolean") {
      agentLogConfig.mirrorSyscallToConsole = opts.mirrorSyscallToConsole;
    }
    if (typeof opts.maxEntries === "number" && opts.maxEntries >= 10 && opts.maxEntries <= 20_000) {
      agentLogConfig.maxEntries = opts.maxEntries;
      while (agentLogRing.length > agentLogConfig.maxEntries) {
        agentLogRing.shift();
      }
    }
    if (typeof opts.maxMsgLen === "number" && opts.maxMsgLen >= 32 && opts.maxMsgLen <= 65536) {
      agentLogConfig.maxMsgLen = opts.maxMsgLen;
    }
    if (
      typeof opts.maxDebugPerSecond === "number" &&
      opts.maxDebugPerSecond >= 1 &&
      opts.maxDebugPerSecond <= 10_000
    ) {
      agentLogConfig.maxDebugPerSecond = opts.maxDebugPerSecond;
    }
    postWorkerAgentLogConfig();

    if (opts.persistToSessionStorage && typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(
        AGENT_SESSION_STORAGE_KEY,
        JSON.stringify({
          channels: agentLogConfig.channels,
          mirrorSyscallToConsole: agentLogConfig.mirrorSyscallToConsole,
          maxEntries: agentLogConfig.maxEntries,
          maxMsgLen: agentLogConfig.maxMsgLen,
          maxDebugPerSecond: agentLogConfig.maxDebugPerSecond,
        }),
      );
    }
    if (opts.clearSessionAgentLogConfig && typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(AGENT_SESSION_STORAGE_KEY);
    }

    const mirror = document.getElementById("agent-log-mirror-syscall") as HTMLInputElement | null;
    if (mirror) mirror.checked = agentLogConfig.mirrorSyscallToConsole;
    for (const k of ["lifecycle", "worker", "syscall"] as const) {
      const sel = document.getElementById(`agent-ch-${k}`) as HTMLSelectElement | null;
      if (sel) sel.value = agentLogConfig.channels[k];
    }
    syncAgentLogTextarea();
  }

  function requestWorkerSnapshot(): Promise<WorkerSnapshot> {
    return new Promise<WorkerSnapshot>((resolve, reject) => {
      const requestId = nextRequestId++;
      const timeout = setTimeout(() => {
        pendingWorkerSnapshot.delete(requestId);
        reject(new Error("getWorkerSnapshot: timeout"));
      }, 10_000);
      pendingWorkerSnapshot.set(requestId, (snapshot) => {
        clearTimeout(timeout);
        resolve(snapshot);
      });
      kernelWorker.postMessage({ type: "debug-worker-snapshot", requestId });
    });
  }

  function countOutputLogOccurrences(needle: string): number {
    if (needle.length === 0) return 0;
    let count = 0;
    let offset = 0;
    while (offset < debugState.outputLog.length) {
      const next = debugState.outputLog.indexOf(needle, offset);
      if (next === -1) {
        return count;
      }
      count += 1;
      offset = next + needle.length;
    }
    return count;
  }

  async function resolveShellKernelPid(): Promise<number> {
    const snapshot = await requestWorkerSnapshot();
    const liveProcessPids = snapshot.workers
      .filter((row) => row.mode === "process" && row.kernelPid > 0)
      .map((row) => row.kernelPid);
    if (shellPid > 0 && liveProcessPids.includes(shellPid)) {
      return shellPid;
    }
    if (liveProcessPids.length === 1) {
      return liveProcessPids[0]!;
    }
    throw new Error("quickTraceShell: tracked shell PID unavailable");
  }

  async function injectStdinUntilOutputContains(
    inputLine: string,
    needle: string,
    timeoutMs = 20_000,
  ): Promise<boolean> {
    if (!booted) {
      await Promise.race([
        whenBooted,
        new Promise<void>((_, reject) => {
          setTimeout(() => reject(new Error("injectStdinUntilOutputContains: not booted")), timeoutMs);
        }),
      ]);
    }
    const seenCount = countOutputLogOccurrences(needle);
    const text = inputLine.endsWith("\n") ? inputLine : `${inputLine}\n`;
    injectStdin(text);
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      if (countOutputLogOccurrences(needle) > seenCount) return true;
      await new Promise((r) => setTimeout(r, 40));
    }
    return false;
  }

  async function collectRuntimeCapabilities(): Promise<WasmosEnvironmentCapabilities> {
    let storageEstimateBytes: number | null = null;
    let storagePersisted: boolean | null = null;
    if (typeof navigator !== "undefined" && navigator.storage?.estimate) {
      try {
        const est = await navigator.storage.estimate();
        storageEstimateBytes = typeof est.usage === "number" ? est.usage : null;
      } catch {
        storageEstimateBytes = null;
      }
    }
    if (typeof navigator !== "undefined" && navigator.storage?.persisted) {
      try {
        storagePersisted = await navigator.storage.persisted();
      } catch {
        storagePersisted = null;
      }
    }
    let artifactManifestPresent = false;
    let imageManifestPresent = false;
    const origin =
      typeof location !== "undefined" && typeof location.origin === "string" ? location.origin : "";
    if (origin) {
      try {
        const r = await fetch(`${origin}/artifact-manifest.json`, { cache: "no-store" });
        artifactManifestPresent = r.ok;
      } catch {
        artifactManifestPresent = false;
      }
      try {
        const r = await fetch(`${origin}/image-manifest.json`, { cache: "no-store" });
        imageManifestPresent = r.ok;
      } catch {
        imageManifestPresent = false;
      }
    }
    return {
      agentHooksProtocol: AGENT_HOOKS_PROTOCOL,
      testHooksEnabled,
      crossOriginIsolated:
        typeof crossOriginIsolated !== "undefined" && crossOriginIsolated === true,
      sharedArrayBuffer: typeof SharedArrayBuffer !== "undefined",
      opfsGetDirectory:
        typeof navigator !== "undefined" && typeof navigator.storage?.getDirectory === "function",
      kernelWasmPath: KERNEL_PATH,
      storageEstimateBytes,
      storagePersisted,
      artifactManifestPresent,
      imageManifestPresent,
      wasmFunctionConstructor:
        typeof WebAssembly !== "undefined" && typeof WebAssembly.Function === "function",
    };
  }

  function closePrototypePanel(): void {
    const panel = document.getElementById("prototype-panel");
    if (panel instanceof HTMLDetailsElement) panel.open = false;
  }

  const isBooted = (): boolean => booted;
  const waitBooted = (timeoutMs = 60_000): Promise<void> =>
      Promise.race([
        whenBooted,
        new Promise<void>((_, reject) => {
          setTimeout(() => reject(new Error("waitBooted: timeout")), timeoutMs);
        }),
      ]);
  const focusTerminal = (): void => {
    platform.terminal.focus();
  };
  const openPrototypePanel = (): void => {
      const panel = document.getElementById("prototype-panel");
      if (panel && panel instanceof HTMLDetailsElement) panel.open = true;
      panel?.scrollIntoView({ block: "nearest" });
    };
  const getTerminalSize = () => platform.terminal.size;
  const setFontSize = (px: number): void => {
    platform.terminal.setFontSizePx(px);
  };
  const getFontSize = (): number => platform.terminal.getFontSizePx();
  const getOutputLog = (): string => debugState.outputLog;
  const clearOutputLog = (): void => {
      debugState.outputLog = "";
    };
  const getAgentLog = (query?: AgentLogQuery): AgentLogEntry[] => filterAgentLogRing(query);
  const getLatestAgentLogSeq = (): number => (agentLogRing.length ? agentLogRing[agentLogRing.length - 1]!.seq : 0);
  const clearAgentLog = (): void => {
      agentLogRing.length = 0;
      agentLogSeqMain = 0;
      syncAgentLogTextarea();
    };
  const setAgentLogConfig = (opts: SetAgentLogConfigOpts): void => {
      applyAgentLogConfigPatch(opts);
    };
  const getAgentLogConfig = (): AgentLogConfigSnapshot => ({
      channels: { ...agentLogConfig.channels },
      mirrorSyscallToConsole: agentLogConfig.mirrorSyscallToConsole,
      maxEntries: agentLogConfig.maxEntries,
      maxMsgLen: agentLogConfig.maxMsgLen,
      maxDebugPerSecond: agentLogConfig.maxDebugPerSecond,
    });
  const getWorkerSnapshot = (): Promise<WorkerSnapshot> => requestWorkerSnapshot();
  const onAgentLog = (handler: (entry: AgentLogEntry) => void): (() => void) => {
      agentLogSubscribers.add(handler);
      return () => {
        agentLogSubscribers.delete(handler);
      };
    };
  const waitForAgentLog = (
    predicate: (entry: AgentLogEntry) => boolean,
    options?: { timeoutMs?: number; intervalMs?: number },
  ): Promise<AgentLogEntry> =>
      new Promise<AgentLogEntry>((resolve, reject) => {
        const timeoutMs = options?.timeoutMs ?? 30_000;
        const intervalMs = options?.intervalMs ?? 40;
        const deadline = Date.now() + timeoutMs;
        const tick = (): void => {
          for (let i = agentLogRing.length - 1; i >= 0; i -= 1) {
            const row = agentLogRing[i]!;
            if (predicate(row)) {
              resolve(cloneAgentLogEntry(row));
              return;
            }
          }
          if (Date.now() >= deadline) {
            reject(new Error("waitForAgentLog: timeout"));
            return;
          }
          setTimeout(tick, intervalMs);
        };
        tick();
      });
  const waitForOutputContains = (
    needle: string,
    options?: { timeoutMs?: number; intervalMs?: number },
  ): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        const timeoutMs = options?.timeoutMs ?? 30_000;
        const intervalMs = options?.intervalMs ?? 40;
        const deadline = Date.now() + timeoutMs;
        const seenCount = countOutputLogOccurrences(needle);
        const tick = (): void => {
          if (countOutputLogOccurrences(needle) > seenCount) {
            resolve();
            return;
          }
          if (Date.now() >= deadline) {
            reject(new Error("waitForOutputContains: timeout"));
            return;
          }
          setTimeout(tick, intervalMs);
        };
        tick();
      });
  const waitForBootLine = (needle = "Boot complete", timeoutMs = 60_000): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        const deadline = Date.now() + timeoutMs;
        const seenCount = countOutputLogOccurrences(needle);
        const tick = (): void => {
          if (countOutputLogOccurrences(needle) > seenCount) {
            resolve();
            return;
          }
          if (Date.now() >= deadline) {
            reject(new Error("waitForBootLine: timeout"));
            return;
          }
          setTimeout(tick, 40);
        };
        tick();
      });
  const inspectAgentRuntime = async (): Promise<AgentRuntimeInspection> => {
      const snapshot = await requestWorkerSnapshot();
      const capabilities = await collectRuntimeCapabilities();
      return {
        protocol: AGENT_HOOKS_PROTOCOL,
        booted,
        capabilities,
        agentLog: {
          entryCount: agentLogRing.length,
          latestSeq: agentLogRing.length ? agentLogRing[agentLogRing.length - 1]!.seq : 0,
          config: {
            channels: { ...agentLogConfig.channels },
            mirrorSyscallToConsole: agentLogConfig.mirrorSyscallToConsole,
            maxEntries: agentLogConfig.maxEntries,
            maxMsgLen: agentLogConfig.maxMsgLen,
            maxDebugPerSecond: agentLogConfig.maxDebugPerSecond,
          },
        },
        terminal: platform.terminal.size,
        fontSizePx: platform.terminal.getFontSizePx(),
        outputLogCharacters: debugState.outputLog.length,
        snapshot,
      };
    };
  const quickTraceShell = async (opts?: {
    kernelPid?: number;
    traceLimit?: number;
    mirrorToConsole?: boolean;
  }): Promise<{ kernelPid: number; traceLimit: number }> => {
      applyAgentLogConfigPatch({
        channels: { lifecycle: "info", worker: "warn", syscall: "debug" },
        mirrorSyscallToConsole: opts?.mirrorToConsole ?? false,
      });
      const pid = opts?.kernelPid && opts.kernelPid > 0 ? opts.kernelPid : await resolveShellKernelPid();
      if (!pid || pid <= 0) {
        throw new Error("quickTraceShell: no process kernelPid resolved");
      }
      const traceLimit = opts?.traceLimit ?? 200;
      await debugState.tracePid(pid, traceLimit);
      return { kernelPid: pid, traceLimit };
    };
  const exportDebugBundle = async (opts?: {
    agentLogLimit?: number;
    outputLogMaxChars?: number;
  }): Promise<Record<string, unknown>> => {
      const agentLogLimit = Math.min(5000, Math.max(1, opts?.agentLogLimit ?? 500));
      const outputLogMaxChars = Math.min(
        500_000,
        Math.max(256, opts?.outputLogMaxChars ?? 100_000),
      );
      const snapshot = await requestWorkerSnapshot();
      const caps = await collectRuntimeCapabilities();
      const out = debugState.outputLog;
      const tail = out.length > outputLogMaxChars ? out.slice(-outputLogMaxChars) : out;
      return {
        exportedAt: Date.now(),
        protocol: AGENT_HOOKS_PROTOCOL,
        capabilities: caps,
        agentLogConfig: {
          channels: { ...agentLogConfig.channels },
          mirrorSyscallToConsole: agentLogConfig.mirrorSyscallToConsole,
          maxEntries: agentLogConfig.maxEntries,
          maxMsgLen: agentLogConfig.maxMsgLen,
          maxDebugPerSecond: agentLogConfig.maxDebugPerSecond,
        },
        agentLog: filterAgentLogRing({ limit: agentLogLimit }),
        outputLog: tail,
        outputLogTruncated: out.length > tail.length,
        outputLogTotalChars: out.length,
        workerSnapshot: snapshot,
        terminal: platform.terminal.size,
        fontSizePx: platform.terminal.getFontSizePx(),
        booted,
      };
    };
  const downloadDebugBundle = async (opts?: {
    agentLogLimit?: number;
    outputLogMaxChars?: number;
  }): Promise<void> => {
      const payload = await exportDebugBundle(opts);
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wasmos-debug-bundle-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    };
  const shellApi = freezeApi<WasmOSShellApi>({
    isBooted,
    waitBooted,
    restartShell: restartShellNow,
  });

  const terminalApi = freezeApi<WasmOSTerminalApi>({
    injectStdin,
    injectStdinUntilOutputContains,
    focusTerminal,
    getTerminalSize,
    setFontSize,
    getFontSize,
    getOutputLog,
    clearOutputLog,
    waitForOutputContains,
    waitForBootLine,
    dispatchMouseEventForTest: platform.terminal.dispatchMouseEventForTest?.bind(platform.terminal),
    encodeMouseEventForTest: platform.terminal.encodeMouseEventForTest?.bind(platform.terminal),
  });

  const agentLogApi = freezeApi<WasmOSAgentLogApi>({
    getAgentLog,
    getLatestAgentLogSeq,
    clearAgentLog,
    setAgentLogConfig,
    getAgentLogConfig,
    onAgentLog,
    waitForAgentLog,
  });

  const debugApi: WasmOSDebugApi = {
    getOutputLog: () => debugState.outputLog,
    seedFile: debugState.seedFile,
    spawnWasm: debugState.spawnWasm,
    tracePid: debugState.tracePid,
    getWorkerSnapshot,
    inspectAgentRuntime,
    quickTraceShell,
    exportDebugBundle,
    downloadDebugBundle,
    openPrototypePanel,
    closePrototypePanel,
  };
  if (testHooksEnabled) {
    debugApi.appendOutputLogForTest = (text: string) => {
      debugState.outputLog += text;
    };
  }
  const frozenDebugApi = freezeApi<WasmOSDebugApi>(debugApi);

  const storageApi = freezeApi<WasmOSStorageApi>({
    flushOpfs: requestOpfsFlush,
    wipeOpfs: requestOpfsWipe,
  });

  const wasmosApi = freezeApi<WasmOSApi>({
    AGENT_HOOKS_PROTOCOL,
    shell: shellApi,
    terminal: terminalApi,
    agentLog: agentLogApi,
    debug: frozenDebugApi,
    storage: storageApi,
  });
  installReadOnlyGlobal("__wasmos", wasmosApi);

  kernelWorker.onmessage = (event: MessageEvent) => {
    const msg = event.data;
    switch (msg.type) {
      case "kernel-starting":
        platform.setStatus("Kernel booting...");
        break;
      case "kernel-booted":
        booted = true;
        resolveWhenBooted?.();
        resolveWhenBooted = null;
        platform.setStatus("Kernel running", "pass");
        platform.terminal.setBooted();
        void spawnShell();
        break;
      case "boot-error":
        platform.setStatus(`Boot error: ${msg.error}`, "fail");
        break;
      case "output":
        debugState.outputLog += msg.text as string;
        platform.terminal.write(msg.text);
        break;
      case "audio-data":
        audio?.playChunk(msg.pcm as ArrayBuffer);
        break;
      case "spawned":
        {
          const requestId = Number(msg.requestId ?? 0);
          const resolve = pendingSpawn.get(requestId);
          if (resolve) {
            resolve(msg.pid as number);
          } else if ((msg.pid as number) > 0) {
            kernelWorker.postMessage({ type: "kill", pid: msg.pid as number });
          }
        }
        break;
      case "debug-seed-file-done":
        {
          const requestId = Number(msg.requestId ?? 0);
          const resolve = pendingDebugSeed.get(requestId);
          if (resolve) {
            pendingDebugSeed.delete(requestId);
            resolve({
              path: String(msg.path ?? ""),
              error: typeof msg.error === "string" ? msg.error : undefined,
            });
          }
        }
        break;
      case "debug-syscall-trace-done":
        {
          const requestId = Number(msg.requestId ?? 0);
          const resolve = pendingDebugTrace.get(requestId);
          if (resolve) {
            pendingDebugTrace.delete(requestId);
            resolve({
              kernelPid: Number(msg.kernelPid ?? 0) | 0,
              limit: Number(msg.limit ?? 0) | 0,
            });
          }
        }
        break;
      case "flush-opfs-done":
        {
          const requestId = Number(msg.requestId ?? 0);
          const resolve = pendingOpfsFlush.get(requestId);
          if (resolve) {
            pendingOpfsFlush.delete(requestId);
            resolve();
          }
        }
        break;
      case "wipe-opfs-done":
        {
          const requestId = Number(msg.requestId ?? 0);
          const finish = pendingOpfsWipe.get(requestId);
          if (finish) {
            pendingOpfsWipe.delete(requestId);
            finish({
              error: typeof msg.error === "string" ? msg.error : undefined,
            });
          }
        }
        break;
      case "agent-log": {
        const raw = msg.entry as AgentLogEntry;
        pushAgentLogEntry({
          ...raw,
          seq: ++agentLogSeqMain,
          data: raw.data,
        });
        break;
      }
      case "debug-worker-snapshot-done": {
        const requestId = Number(msg.requestId ?? 0);
        const finish = pendingWorkerSnapshot.get(requestId);
        if (finish) {
          pendingWorkerSnapshot.delete(requestId);
          finish(msg.snapshot as WorkerSnapshot);
        }
        break;
      }
    }
  };

  kernelWorker.onerror = (err: ErrorEvent) => {
    platform.setStatus(`Worker error: ${err.message}`, "fail");
  };

  platform.terminal.onInput((text) => {
    if (text.length === 0) return;
    wakeKernelWorker();
    kernelWorker.postMessage({ type: "input", data: text });
  });

  platform.terminal.onKeyboardEvent((packet) => {
    wakeKernelWorker();
    kernelWorker.postMessage({ type: "keyboard-event", data: packet.buffer }, [packet.buffer]);
  });

  platform.terminal.onResize((cols, rows) => {
    kernelWorker.postMessage({ type: "console-resize", cols, rows });
  });

  platform.onShellChange((_shell) => {
    if (!booted) return;
    if (shellPid > 0) {
      kernelWorker.postMessage({ type: "kill", pid: shellPid });
      shellPid = -1;
    }
    void spawnShell();
  });

  const { cols, rows } = platform.terminal.size;
  kernelWorker.postMessage({ type: "console-resize", cols, rows });

  postWorkerAgentLogConfig();
  kernelWorker.postMessage({ type: "boot", sab, kernelWasm }, [kernelWasm]);

  addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void requestOpfsFlush();
    }
  });
  addEventListener("pagehide", () => {
    void requestOpfsFlush();
  });
  addEventListener("beforeunload", () => {
    void requestOpfsFlush();
  });

  setTimeout(() => {
    platform.terminal.applyMeasuredSize();
  }, 0);

  const wireClick = (id: string, handler: () => void) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener("click", handler);
  };
  wireClick("proto-font-minus", () => {
    platform.terminal.setFontSizePx(platform.terminal.getFontSizePx() - 1);
  });
  wireClick("proto-font-plus", () => {
    platform.terminal.setFontSizePx(platform.terminal.getFontSizePx() + 1);
  });
  wireClick("proto-clear-term", () => {
    platform.terminal.clear();
  });
  wireClick("proto-clear-log", () => {
    debugState.outputLog = "";
  });
  wireClick("proto-flush-opfs", () => {
    void requestOpfsFlush();
  });
  wireClick("proto-wipe-opfs", () => {
    void requestOpfsWipe().catch((err) => console.warn("[wasmos] wipe OPFS failed:", err));
  });
  wireClick("proto-restart-shell", () => {
    restartShellNow();
  });
  wireClick("proto-copy-log", () => {
    void navigator.clipboard.writeText(debugState.outputLog).catch((err) => {
      console.warn("[wasmos] copy log failed:", err);
    });
  });
  wireClick("proto-download-log", () => {
    const blob = new Blob([debugState.outputLog], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wasmos-output-log-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  });
  wireClick("proto-export-debug", () => {
    void debugApi.downloadDebugBundle().catch((err) => {
      console.warn("[wasmos] debug bundle export failed:", err);
    });
  });

  addEventListener(
    "keydown",
    (ev: KeyboardEvent) => {
      if (ev.key !== "Escape") return;
      const panel = document.getElementById("prototype-panel");
      if (!(panel instanceof HTMLDetailsElement) || !panel.open) return;
      panel.open = false;
      ev.preventDefault();
    },
    true,
  );

  const readChannelSelects = (): Partial<AgentLogConfigSnapshot> => {
    const channels = { ...agentLogConfig.channels };
    for (const k of ["lifecycle", "worker", "syscall"] as const) {
      const sel = document.getElementById(`agent-ch-${k}`) as HTMLSelectElement | null;
      if (!sel) continue;
      const v = sel.value;
      if (v === "off" || v === "error" || v === "warn" || v === "info" || v === "debug") {
        channels[k] = v;
      }
    }
    return { channels };
  };

  wireClick("agent-log-copy", () => {
    const text = agentLogRing.map((e) => JSON.stringify(e)).join("\n");
    void navigator.clipboard.writeText(text).catch((err) => {
      console.warn("[wasmos] agent log copy failed:", err);
    });
  });
  wireClick("agent-log-clear", () => {
    agentLogApi.clearAgentLog();
  });
  wireClick("agent-preset-quiet", () => {
    applyAgentLogConfigPatch({
      channels: { lifecycle: "off", worker: "off", syscall: "off" },
      mirrorSyscallToConsole: false,
    });
  });
  wireClick("agent-preset-default", () => {
    applyAgentLogConfigPatch({
      channels: {
        lifecycle: "info",
        worker: "warn",
        syscall: "off",
      },
      mirrorSyscallToConsole: false,
    });
  });
  wireClick("agent-preset-trace", () => {
    applyAgentLogConfigPatch({
      channels: {
        lifecycle: "info",
        worker: "warn",
        syscall: "debug",
      },
      mirrorSyscallToConsole: false,
    });
  });

  for (const k of ["lifecycle", "worker", "syscall"] as const) {
    const sel = document.getElementById(`agent-ch-${k}`) as HTMLSelectElement | null;
    if (sel) {
      sel.addEventListener("change", () => {
        applyAgentLogConfigPatch(readChannelSelects());
      });
    }
  }
  const mirrorEl = document.getElementById("agent-log-mirror-syscall") as HTMLInputElement | null;
  if (mirrorEl) {
    mirrorEl.checked = agentLogConfig.mirrorSyscallToConsole;
    mirrorEl.addEventListener("change", () => {
      applyAgentLogConfigPatch({ mirrorSyscallToConsole: mirrorEl.checked });
    });
  }
  for (const k of ["lifecycle", "worker", "syscall"] as const) {
    const sel = document.getElementById(`agent-ch-${k}`) as HTMLSelectElement | null;
    if (sel) sel.value = agentLogConfig.channels[k];
  }
  syncAgentLogTextarea();
}

main().catch((err) => {
  console.error("Bootloader fatal error:", err);
});
